import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Building, Building2, MapPin, Globe, Calendar, Plus, Search, 
  RefreshCw, Filter, TrendingUp, Users, CheckCircle, XCircle,
  Eye, Edit, MoreVertical, Phone, Mail, UserPlus
} from 'lucide-react';
import { supabase } from '../utils/supabaseClient';
import '../styles/GalleriesAdmin.css';

const GalleriesAdmin = () => {
  const navigate = useNavigate();
  
  // State management
  const [galleries, setGalleries] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchTimer = useRef(null);
  const [statusFilter, setStatusFilter] = useState('approved');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [activeTab, setActiveTab] = useState('overview');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalGalleries, setTotalGalleries] = useState(0);
  const [pageSize] = useState(20);

  // Debounce search input
  useEffect(() => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 400);
    return () => clearTimeout(searchTimer.current);
  }, [searchTerm]);

  // Load galleries data
  useEffect(() => {
    loadGalleriesData();
  }, [debouncedSearch, statusFilter, typeFilter, sortBy, sortOrder, currentPage]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, statusFilter, typeFilter]);

  const loadGalleriesData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const [galleriesData, analyticsData] = await Promise.all([
        getGalleriesOverview(),
        getGalleryAnalytics()
      ]);

      setGalleries(galleriesData);
      setAnalytics(analyticsData);
    } catch (error) {
      console.error('Error loading galleries data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  };

  const getGalleriesOverview = async () => {
    try {
      // Build the base query for counting (without pagination)
      let countQuery = supabase
        .from('galleries')
        .select('gallery_id', { count: 'exact', head: true });

      // Build the main query for data
      let query = supabase
        .from('galleries')
        .select(`
          gallery_id,
          name,
          type,
          description,
          address,
          website,
          contact_email,
          contact_phone,
          approved,
          created_at,
          updated_at,
          cover_image,
          logo,
          added_by
        `);

      // Add search filter to both queries
      if (debouncedSearch) {
        const searchFilter = `name.ilike.%${debouncedSearch}%,description.ilike.%${debouncedSearch}%,address.ilike.%${debouncedSearch}%`;
        countQuery = countQuery.or(searchFilter);
        query = query.or(searchFilter);
      }

      // Add status filter to both queries
      if (statusFilter !== 'all') {
        const isApproved = statusFilter === 'approved';
        countQuery = countQuery.eq('approved', isApproved);
        query = query.eq('approved', isApproved);
      }

      // Add type filter to both queries
      if (typeFilter !== 'all') {
        countQuery = countQuery.eq('type', typeFilter);
        query = query.eq('type', typeFilter);
      }

      // Get the total count first
      const { count: totalCount, error: countError } = await countQuery;
      if (countError) throw countError;

      setTotalGalleries(totalCount || 0);

      // Add sorting and pagination to the main query
      const orderColumn = sortBy === 'collections' || sortBy === 'events' ? 'name' : sortBy;
      query = query.order(orderColumn, { ascending: sortOrder === 'asc' });

      // Add pagination
      const from = (currentPage - 1) * pageSize;
      const to = from + pageSize - 1;
      query = query.range(from, to);

      const { data: galleries, error } = await query;
      if (error) throw error;

      // Get collection and event counts for each gallery
      const galleriesWithCounts = await Promise.all(
        (galleries || []).map(async (gallery) => {
          // Get collection count
          const { count: collectionCount } = await supabase
            .from('gallery_collection_mapping')
            .select('*', { count: 'exact', head: true })
            .eq('gallery_id', gallery.gallery_id);

          // Get event count
          const { count: eventCount } = await supabase
            .from('events')
            .select('*', { count: 'exact', head: true })
            .eq('gallery_id', gallery.gallery_id);

          return {
            ...gallery,
            id: gallery.gallery_id, // Normalize the ID field
            collection_count: collectionCount || 0,
            event_count: eventCount || 0
          };
        })
      );

      // If sorting by collections or events, sort the results with counts
      if (sortBy === 'collections' || sortBy === 'events') {
        galleriesWithCounts.sort((a, b) => {
          const aValue = sortBy === 'collections' ? a.collection_count : a.event_count;
          const bValue = sortBy === 'collections' ? b.collection_count : b.event_count;
          return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
        });
      }

      return galleriesWithCounts;
    } catch (error) {
      console.error('Error fetching galleries:', error);
      throw error;
    }
  };

  const getGalleryAnalytics = async () => {
    try {
      // Get total counts
      const { count: totalGalleries } = await supabase
        .from('galleries')
        .select('*', { count: 'exact', head: true });

      const { count: approvedGalleries } = await supabase
        .from('galleries')
        .select('*', { count: 'exact', head: true })
        .eq('approved', true);

      const { count: pendingGalleries } = await supabase
        .from('galleries')
        .select('*', { count: 'exact', head: true })
        .eq('approved', false);

      // Get recent galleries (last 30 days)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      const { count: recentGalleries } = await supabase
        .from('galleries')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', thirtyDaysAgo.toISOString());

      // Get galleries with websites
      const { count: galleriesWithWebsites } = await supabase
        .from('galleries')
        .select('*', { count: 'exact', head: true })
        .not('website', 'is', null);

      // Get artist-submitted galleries (pending review)
      const { count: artistSubmittedPending } = await supabase
        .from('galleries')
        .select('*', { count: 'exact', head: true })
        .not('added_by', 'is', null)
        .eq('approved', false);

      return {
        total_galleries: totalGalleries || 0,
        approved_galleries: approvedGalleries || 0,
        pending_galleries: pendingGalleries || 0,
        recent_galleries: recentGalleries || 0,
        galleries_with_websites: galleriesWithWebsites || 0,
        artist_submitted_pending: artistSubmittedPending || 0
      };
    } catch (error) {
      console.error('Error fetching gallery analytics:', error);
      throw error;
    }
  };

  // Handle approve gallery
  const handleApproveGallery = async (galleryId) => {
    try {
      const { error } = await supabase
        .from('galleries')
        .update({ approved: true })
        .eq('gallery_id', galleryId);

      if (error) throw error;
      
      // Refresh the list
      loadGalleriesData();
    } catch (error) {
      console.error('Error approving gallery:', error);
      setError(`Failed to approve gallery: ${error.message}`);
    }
  };

  // Handle reject gallery (delete or mark as rejected)
  const handleRejectGallery = async (galleryId) => {
    const confirmed = window.confirm('Are you sure you want to reject this gallery? This will delete the gallery.');
    if (!confirmed) return;

    try {
      const { error } = await supabase
        .from('galleries')
        .delete()
        .eq('gallery_id', galleryId);

      if (error) throw error;
      
      // Refresh the list
      loadGalleriesData();
    } catch (error) {
      console.error('Error rejecting gallery:', error);
      setError(`Failed to reject gallery: ${error.message}`);
    }
  };

  // Get unique gallery types for filter (from all galleries, not just current page)
  const [allGalleryTypes, setAllGalleryTypes] = useState([]);
  
  useEffect(() => {
    const loadGalleryTypes = async () => {
      try {
        const { data } = await supabase
          .from('galleries')
          .select('type')
          .not('type', 'is', null);
        
        const types = [...new Set(data?.map(g => g.type).filter(Boolean))];
        setAllGalleryTypes(types.sort());
      } catch (error) {
        console.error('Error loading gallery types:', error);
      }
    };
    
    loadGalleryTypes();
  }, []);

  if (initialLoad && loading) {
    return (
      <div className="galleries-admin">
        <div className="galleries-loading">
          <RefreshCw className="spinning" />
          <p>Loading galleries...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="galleries-admin">
        <div className="galleries-error">
          <div className="galleries-empty">
            <Building className="galleries-empty-icon" />
            <h3 className="galleries-empty-title">Error Loading Galleries</h3>
            <p className="galleries-empty-description">{error}</p>
            <button
              onClick={loadGalleriesData}
              className="galleries-btn galleries-btn-primary"
            >
              <RefreshCw className="galleries-btn-icon" />
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="galleries-admin">
      <div className="galleries-admin-container">
        {/* Header */}
        <div className="galleries-admin-header">
          <div className="galleries-header-content">
            <h1 className="galleries-admin-title">
              <Building className="galleries-tab-icon" color="#007bff" />
              Galleries & Venues
            </h1>
            <p className="galleries-admin-subtitle">
              Manage galleries, venues, and exhibition spaces across the platform
            </p>
          </div>
          <div className="galleries-header-actions">
            <button
              onClick={() => navigate('/admin/galleries/create')}
              className="galleries-btn galleries-btn-primary"
            >
              <Plus className="galleries-btn-icon" color="#ffffff" />
              New Gallery
            </button>
            <button
              onClick={loadGalleriesData}
              className="galleries-btn galleries-btn-secondary"
              title="Refresh Galleries List"
              disabled={loading}
            >
              <RefreshCw className={`galleries-btn-icon ${loading ? 'spinning' : ''}`} color="#6c757d" />
              Refresh
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="galleries-tab-navigation">
          <ul className="galleries-tab-list">
            {[
              { id: 'overview', label: 'Galleries Overview', icon: Building },
              { id: 'analytics', label: 'Analytics', icon: TrendingUp }
            ].map((tab) => (
              <li key={tab.id}>
                <button
                  onClick={() => setActiveTab(tab.id)}
                  className={`galleries-tab-button ${activeTab === tab.id ? 'active' : ''}`}
                >
                  <tab.icon className="galleries-tab-icon" color={activeTab === tab.id ? '#007bff' : '#6c757d'} />
                  {tab.label}
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className="galleries-tab-content">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="galleries-overview">
              {/* Stats Cards */}
              <div className="galleries-stats-grid">
                <div className="galleries-stat-card">
                  <div className="galleries-stat-icon galleries">
                    <Building />
                  </div>
                  <div className="galleries-stat-details">
                    <span className="galleries-stat-value">{analytics?.total_galleries || 0}</span>
                    <span className="galleries-stat-label">Total Galleries</span>
                  </div>
                </div>
                <div className="galleries-stat-card">
                  <div className="galleries-stat-icon approved">
                    <CheckCircle />
                  </div>
                  <div className="galleries-stat-details">
                    <span className="galleries-stat-value">{analytics?.approved_galleries || 0}</span>
                    <span className="galleries-stat-label">Approved</span>
                  </div>
                </div>
                <div className="galleries-stat-card">
                  <div className="galleries-stat-icon pending">
                    <XCircle />
                  </div>
                  <div className="galleries-stat-details">
                    <span className="galleries-stat-value">{analytics?.pending_galleries || 0}</span>
                    <span className="galleries-stat-label">Pending</span>
                  </div>
                </div>
                <div className="galleries-stat-card">
                  <div className="galleries-stat-icon recent">
                    <Calendar />
                  </div>
                  <div className="galleries-stat-details">
                    <span className="galleries-stat-value">{analytics?.recent_galleries || 0}</span>
                    <span className="galleries-stat-label">Recent (30d)</span>
                  </div>
                </div>
                {analytics?.artist_submitted_pending > 0 && (
                  <div className="galleries-stat-card artist-submitted">
                    <div className="galleries-stat-icon artist">
                      <UserPlus />
                    </div>
                    <div className="galleries-stat-details">
                      <span className="galleries-stat-value">{analytics?.artist_submitted_pending || 0}</span>
                      <span className="galleries-stat-label">Artist Submitted</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Search and Filters */}
              <div className="galleries-search-filters">
                <div className="galleries-search-box">
                  <Search className="search-icon" />
                  <input
                    type="text"
                    placeholder="Search galleries by name, description, or address..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input"
                  />
                </div>
                <div className="galleries-filters-row">
                  <div className="galleries-filter-select">
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="galleries-filter-dropdown"
                    >
                      <option value="all">All Status</option>
                      <option value="approved">Approved</option>
                      <option value="pending">Pending Approval</option>
                    </select>
                  </div>
                  <div className="galleries-filter-select">
                    <select
                      value={typeFilter}
                      onChange={(e) => setTypeFilter(e.target.value)}
                      className="galleries-filter-dropdown"
                    >
                      <option value="all">All Types</option>
                      {allGalleryTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                  <div className="galleries-sort-controls">
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="galleries-filter-dropdown"
                    >
                      <option value="name">Name</option>
                      <option value="type">Type</option>
                      <option value="address">Location</option>
                      <option value="created_at">Date Added</option>
                      <option value="collections">Collections</option>
                      <option value="events">Events</option>
                    </select>
                    <button
                      onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                      className="sort-order-button"
                      title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
                    >
                      {sortOrder === 'asc' ? '↑' : '↓'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Galleries List */}
              <div className="galleries-list-container" style={{ position: 'relative' }}>
                {loading && !initialLoad && (
                  <div className="galleries-list-loading-overlay">
                    <RefreshCw className="spinning" size={20} />
                  </div>
                )}
                {galleries.length === 0 ? (
                  <div className="galleries-empty">
                    <Building className="galleries-empty-icon" />
                    <h3 className="galleries-empty-title">No Galleries Found</h3>
                    <p className="galleries-empty-description">
                      {searchTerm || statusFilter !== 'all' || typeFilter !== 'all'
                        ? 'Try adjusting your search or filter criteria'
                        : 'No galleries have been added yet'
                      }
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="galleries-results-info">
                      <span className="results-count">
                        Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalGalleries)} of {totalGalleries} galler{totalGalleries !== 1 ? 'ies' : 'y'}
                      </span>
                      <span className="sort-info">
                        sorted by {sortBy === 'name' ? 'name' : 
                                  sortBy === 'type' ? 'type' :
                                  sortBy === 'address' ? 'location' :
                                  sortBy === 'created_at' ? 'date added' :
                                  sortBy === 'collections' ? 'collection count' :
                                  sortBy === 'events' ? 'event count' : sortBy}
                        {' '}({sortOrder === 'asc' ? 'ascending' : 'descending'})
                      </span>
                    </div>
                    <div className="galleries-table">
                      <div className="galleries-table-header">
                        <div className="header-cell name">Gallery Name</div>
                        <div className="header-cell type">Type</div>
                        <div className="header-cell location">Location</div>
                        <div className="header-cell stats">Collections</div>
                        <div className="header-cell stats">Events</div>
                        <div className="header-cell status">Status</div>
                        <div className="header-cell actions">Actions</div>
                      </div>
                      {galleries.map((gallery) => (
                        <GalleryRow
                          key={gallery.id}
                          gallery={gallery}
                          navigate={navigate}
                          onApprove={handleApproveGallery}
                          onReject={handleRejectGallery}
                        />
                      ))}
                    </div>

                    {/* Pagination */}
                    {totalGalleries > pageSize && (
                      <div className="galleries-pagination">
                        <div className="pagination-info">
                          Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalGalleries)} of {totalGalleries} galleries
                        </div>
                        <div className="pagination-controls">
                          <button 
                            className="galleries-btn galleries-btn-secondary pagination-btn"
                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                            disabled={currentPage === 1}
                          >
                            ← Previous
                          </button>
                          <span className="page-info">
                            Page {currentPage} of {Math.ceil(totalGalleries / pageSize)}
                          </span>
                          <button 
                            className="galleries-btn galleries-btn-secondary pagination-btn"
                            onClick={() => setCurrentPage(prev => Math.min(Math.ceil(totalGalleries / pageSize), prev + 1))}
                            disabled={currentPage === Math.ceil(totalGalleries / pageSize)}
                          >
                            Next →
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* Analytics Tab */}
          {activeTab === 'analytics' && (
            <div className="galleries-analytics">
              <div className="analytics-header">
                <h2>Gallery Analytics</h2>
                <p>Distribution and performance insights</p>
              </div>

              <div className="analytics-grid">
                <div className="analytics-card">
                  <h3>Gallery Distribution</h3>
                  <div className="analytics-metrics">
                    <div className="metric">
                      <span className="metric-label">Total Galleries</span>
                      <span className="metric-value">{analytics?.total_galleries || 0}</span>
                    </div>
                    <div className="metric">
                      <span className="metric-label">Approved</span>
                      <span className="metric-value">{analytics?.approved_galleries || 0}</span>
                    </div>
                    <div className="metric">
                      <span className="metric-label">Pending Review</span>
                      <span className="metric-value">{analytics?.pending_galleries || 0}</span>
                    </div>
                  </div>
                </div>

                <div className="analytics-card">
                  <h3>Gallery Features</h3>
                  <div className="analytics-metrics">
                    <div className="metric">
                      <span className="metric-label">With Websites</span>
                      <span className="metric-value">{analytics?.galleries_with_websites || 0}</span>
                    </div>
                    <div className="metric">
                      <span className="metric-label">Recent Additions</span>
                      <span className="metric-value">{analytics?.recent_galleries || 0}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Gallery Row Component (following EventsAdmin style)
const GalleryRow = ({ gallery, navigate, onApprove, onReject }) => {
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusColor = (approved) => {
    return approved ? 'status-approved' : 'status-pending';
  };

  const handleRowClick = () => {
    navigate(`/admin/galleries/${gallery.id}`);
  };

  const handleEdit = (e) => {
    e.stopPropagation();
    navigate(`/admin/galleries/${gallery.id}`);
  };

  const handleApprove = (e) => {
    e.stopPropagation();
    if (onApprove) onApprove(gallery.id);
  };

  const handleReject = (e) => {
    e.stopPropagation();
    if (onReject) onReject(gallery.id);
  };

  return (
    <div className="gallery-row" onClick={handleRowClick}>
      <div className="row-cell name">
        <div className="gallery-name-wrapper">
          <span className="gallery-name">{gallery.name}</span>
          {gallery.added_by && (
            <span className="artist-submitted-badge" title="Submitted by an artist">
              <UserPlus size={12} />
              Artist
            </span>
          )}
        </div>
        <div className="gallery-meta">
          {gallery.contact_email && (
            <span className="meta-item">
              <Mail className="meta-icon" />
              {gallery.contact_email}
            </span>
          )}
          {gallery.website && (
            <span className="meta-item">
              <Globe className="meta-icon" />
              Website
            </span>
          )}
        </div>
      </div>
      <div className="row-cell type">
        <div className="gallery-type">{gallery.type || 'Unspecified'}</div>
      </div>
      <div className="row-cell location">
        <div className="gallery-address">
          <MapPin className="meta-icon" />
          {gallery.address || 'No address'}
        </div>
      </div>
      <div className="row-cell stats">
        <span className="stat-number">{gallery.collection_count || 0}</span>
      </div>
      <div className="row-cell stats">
        <span className="stat-number">{gallery.event_count || 0}</span>
      </div>
      <div className="row-cell status">
        <span className={`gallery-status ${getStatusColor(gallery.approved)}`}>
          {gallery.approved ? 'Approved' : 'Pending'}
        </span>
      </div>
      <div className="row-cell actions">
        <div className="action-buttons" onClick={(e) => e.stopPropagation()}>
          <button
            className="action-btn edit"
            onClick={handleEdit}
            title="Edit Gallery"
          >
            <Edit size={16} />
          </button>
          {!gallery.approved && (
            <>
              <button
                className="action-btn approve"
                onClick={handleApprove}
                title="Approve Gallery"
              >
                <CheckCircle size={16} />
              </button>
              <button
                className="action-btn reject"
                onClick={handleReject}
                title="Reject Gallery"
              >
                <XCircle size={16} />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GalleriesAdmin;